import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import '../styles/AllCompsMap.css';
import { GoogleMap, Polygon } from '@react-google-maps/api';
import { MapModeToggle, Marker, NeighbourhoodGradeMap, CompMapBubble } from './';
import { House, MapBubble } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { getPriceField } from '../functions';
import { rasterOnSafari } from '../utils/mapRendering';

function AllCompsMap(props) {
    const subjectProperty = props.subjectProperty;
    const isMapLoaded = props.isMapLoaded;
    const comps = props.comps;
    const clickedZPID = props.clickedZPID;
    const viewComp = props.viewComp;
    // const bestComps = props.bestComps;
    const hoverZPID = props.hoverZPID;
    const neighbourhoodData = props.neighbourhoodData;
    const tracts = props.tracts;
    const neighbourhoodBounds = props.neighbourhoodBounds;
    const hoverMapLegend = props.hoverMapLegend;
    const setHoverMapLegend = props.setHoverMapLegend;
    const showHeatmap = props.showHeatmap;
    const readOnly = props.readOnly;
    const compTypeLegend = props.compTypeLegend;
    const innerWidth = props.innerWidth;
    const mapType = props.mapType;
    const setMapType = props.setMapType;
    const rawMap = props.rawMap;
    const setRawMap = props.setRawMap;
    const ceilings = props.ceilings;
    const floors = useMemo(() => props.floors || [], [props.floors]);
    const staleActives = useMemo(() => props.staleActives || [], [props.staleActives]);
    const setSubjectPropertyHover = props.setSubjectPropertyHover;
    const selectedComps = props.selectedComps;
    const conditionFilterOptions = props.conditionFilterOptions;
    const conditionUpdate = props.conditionUpdate;
    // searchPolygon: faint shaded overlay tracing the compFetcherV1 search area.
    // Only passed when the "All comps" pseudo-market is open in RunCompsV2 — null
    // for every other view (subject market / Best Comps / etc.).
    const searchPolygon = props.searchPolygon;
    const stableCentre = useMemo(() => ({ 
        lat: subjectProperty.addressDetails.latitude, 
        lng: subjectProperty.addressDetails.longitude 
    }), [subjectProperty.addressDetails]);

    const options = useMemo(() => ({
		disableDefaultUI: false,
		scrollwheel: true,
		mapTypeControl: false,
		streetViewControl: false,
        fullscreenControl: false,
        keyboardShortcuts: false,
        mapId: JSON.parse(process.env.REACT_APP_COMPS_MAP_ID).key,
        // Safari cannot hold the WebGL context a vector map needs — see
        // src/utils/mapRendering.js. No-op everywhere else.
        ...rasterOnSafari()
	}), []);

    const onLoad = useCallback((mapInstance) => {
        setRawMap(mapInstance);
    }, [setRawMap]);

    // Memoize start position to avoid creating new object on every render
    const startPosition = useMemo(() => ({
        lat: subjectProperty.addressDetails.latitude,
        lng: subjectProperty.addressDetails.longitude
    }), [subjectProperty.addressDetails.latitude, subjectProperty.addressDetails.longitude]);

    // Memoize check functions to avoid recreating on every render
    const checkBestComp = useCallback((item) => {
        const zpid = item.uniqueIds.zillowId;
        const bestIndex = selectedComps.findIndex(e => e.zpid === zpid && e.type === "arv");
        return bestIndex !== -1;
    }, [selectedComps]);

    const checkCeilingComp = useCallback((item) => {
        const zpid = item.uniqueIds.zillowId;
        const ceilingIndex = ceilings.findIndex(e => e.zpid === zpid);
        return ceilingIndex !== -1;
    }, [ceilings]);

    const checkFloorComp = useCallback((item) => {
        const zpid = item.uniqueIds.zillowId;
        return floors.findIndex(e => e.zpid === zpid) !== -1;
    }, [floors]);

    const checkStaleActiveComp = useCallback((item) => {
        const zpid = item.uniqueIds.zillowId;
        return staleActives.findIndex(e => String(e.zpid) === String(zpid)) !== -1;
    }, [staleActives]);

    const toggleSatellite = useCallback(() => {
        if (rawMap) {
            const newMapType = mapType === "roadmap" ? "satellite" : "roadmap";
            rawMap.setMapTypeId(newMapType);
            setMapType(newMapType);
        }
    }, [rawMap, mapType, setMapType]);

    const hoverTimeoutRef = useRef(null);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const handleSubjectPropertyHover = useCallback(() => {
        setSubjectPropertyHover(true);
    }, [setSubjectPropertyHover]);

    const handleMouseEnter = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            handleSubjectPropertyHover();
        }, 300);
    }, [handleSubjectPropertyHover]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setSubjectPropertyHover(false);
    }, [setSubjectPropertyHover]);

    const checkCompVisible = useCallback((item) => {
        if ( conditionFilterOptions.length === 0 ) {
            return true;
        }
        // Optimization: if all conditions are selected, show everything without string matching
        const allSelected = conditionFilterOptions.every(option => option.selected === true);
        if ( allSelected ) {
            return true;
        }
        else {
            const zpid = item.uniqueIds.zillowId;
            const conditionUpdateIndex = conditionUpdate.findIndex(e => e.zpid === zpid);
            const condition = conditionUpdateIndex !== -1 ? conditionUpdate[conditionUpdateIndex].condition : item.compDetails.propertyCondition;
            const conditionIndex = conditionFilterOptions.findIndex(e => e.label === condition && e.selected === true);
            return conditionIndex !== -1;
        }
    }, [conditionFilterOptions, conditionUpdate]);

    return (
        isMapLoaded === true &&
        <GoogleMap
            zoom={14}
            center={stableCentre}
            mapContainerClassName="all-comps-map-container"
            options={options}
            onLoad={onLoad}
        >
            {
                rawMap && innerWidth >= 1000 &&
                <MapModeToggle
                    toggleSatellite={toggleSatellite}
                    mapType={mapType}
                />
            }
            {
                rawMap && Array.isArray(searchPolygon) && searchPolygon.length > 2 &&
                <Polygon
                    paths={searchPolygon}
                    options={{
                        strokeColor: colour.gray04,
                        strokeOpacity: 0.6,
                        strokeWeight: 1,
                        fillColor: colour.jade02,
                        fillOpacity: 0.06,
                        clickable: false,
                    }}
                />
            }
            {/* Hovered OR Normal property icon for Non-Best properties */}
            {
                rawMap && readOnly === false &&
                comps.map((item) =>
                    item.subjectProperty !== true && checkBestComp(item) === false && checkCompVisible(item) === true &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        onClick={() => viewComp(item)}
                        key={item.uniqueIds.zillowId}
                    >
                        {/* OLD BUBBLE - Commented out
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true ? colour.red01 : colour.gray02}
                                stroke={checkCeilingComp(item) === true ? colour.red03 : colour.gray03}
                                strokeWidth="0.5"
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : colour.gray04}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                        */}
                        <CompMapBubble
                            price={getPriceField(item)}
                            listingStatus={item.listingDetails.listingStatus}
                            isSelected={false}
                            isClicked={clickedZPID === item.uniqueIds.zillowId && hoverZPID === null}
                            isHovered={hoverZPID === item.uniqueIds.zillowId}
                            isCeiling={checkCeilingComp(item)}
                            isFloor={checkFloorComp(item)}
                            isStaleActive={checkStaleActiveComp(item)}
                        />
                    </Marker>
                )
            }
            {/* Hovered OR standard property icon for only Best properties */}
            {
                rawMap && readOnly === false &&
                comps.map((item) =>
                    item.subjectProperty !== true && checkBestComp(item) === true &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        key={item.uniqueIds.zillowId}
                        onClick={() => viewComp(item)}
                    >
                        {/* OLD BUBBLE - Commented out
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? colour.red02 : checkCeilingComp(item) === true ? colour.red01 : hoverZPID === item.uniqueIds.zillowId ? colour.jade02 : colour.jade01}
                                stroke={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? colour.red03 : checkCeilingComp(item) === true ? colour.red02 : hoverZPID === item.uniqueIds.zillowId ? colour.jade03 : colour.jade02}
                                strokeWidth={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? "2" : checkCeilingComp(item) === true ? "0.5" : hoverZPID === item.uniqueIds.zillowId ? "2" : "0.5"}
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : colour.jade03}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                        */}
                        <CompMapBubble
                            price={getPriceField(item)}
                            listingStatus={item.listingDetails.listingStatus}
                            isSelected={true}
                            isClicked={clickedZPID === item.uniqueIds.zillowId && hoverZPID === null}
                            isHovered={hoverZPID === item.uniqueIds.zillowId}
                            isCeiling={checkCeilingComp(item)}
                            isFloor={checkFloorComp(item)}
                            isStaleActive={checkStaleActiveComp(item)}
                        />
                    </Marker>
                )
            }
            {/* Clicked property for Best OR Normal */}
            {
                rawMap && readOnly === false && hoverZPID === null &&
                comps.map((item) =>
                    item.subjectProperty !== true && clickedZPID === item.uniqueIds.zillowId &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        onClick={() => null}
                        key={item.uniqueIds.zillowId}
                    >
                        {/* OLD BUBBLE - Commented out
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true ? colour.red01 : checkBestComp(item) === true ? colour.jade02 : colour.gray04}
                                stroke={checkCeilingComp(item) === true ? colour.red03 : checkBestComp(item) === true ? colour.jade03 : colour.gray05}
                                strokeWidth="2"
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : checkBestComp(item) === true ? colour.jade03 : colour.gray06}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                        */}
                        <CompMapBubble
                            price={getPriceField(item)}
                            listingStatus={item.listingDetails.listingStatus}
                            isSelected={checkBestComp(item)}
                            isClicked={true}
                            isHovered={hoverZPID === item.uniqueIds.zillowId}
                            isCeiling={checkCeilingComp(item)}
                            isFloor={checkFloorComp(item)}
                            isStaleActive={checkStaleActiveComp(item)}
                        />
                    </Marker>
                )
            }
            {/* Shareable link showing different coloured icons */}
            {
                rawMap && readOnly === true && compTypeLegend !== null &&
                compTypeLegend.map((item) =>
                    item.comps.map((comp) =>
                        <Marker
                            map={rawMap}
                            position={{ lat: comp.addressDetails.latitude, lng: comp.addressDetails.longitude }}
                            onClick={() => viewComp(comp)}
                            key={comp.uniqueIds.zillowId}
                        >
                            {/* OLD BUBBLE - Commented out (uses custom compTypeLegend colors)
                            <div className="relative-container">
                                <MapBubble
                                    className=""
                                    fill={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? item.stroke : item.fill}
                                    stroke={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? item.house : item.stroke}
                                    strokeWidth={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? "2" : "0.5"}
                                />
                                <House
                                    fill={item.house}
                                    className="all-comps-map-house-icon"
                                />
                            </div>
                            */}
                            <CompMapBubble
                                price={getPriceField(comp)}
                                listingStatus={comp.listingDetails.listingStatus}
                                isSelected={false}
                                isClicked={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID)}
                                isHovered={comp.uniqueIds.zillowId === hoverZPID}
                            />
                        </Marker>
                    )
                )
            }
            {/* Subject property */}
            {
                rawMap &&
                <Marker
                    map={rawMap}
                    position={startPosition}
                    onClick={() => null}
                >
                    <div 
                        className="relative-container"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <MapBubble
                            className=""
                            fill={colour.gray07}
                            stroke={colour.gray00}
                            strokeWidth="0.5"
                        />
                        <House
                            fill={colour.gray00}
                            className="all-comps-map-house-icon"
                        />
                    </div>
                </Marker>
            }
            {
                showHeatmap === true &&
                <NeighbourhoodGradeMap
                    bounds={neighbourhoodBounds}
                    tracts={tracts}
                    neighbourhoodOption={0}
                    neighbourhoodData={neighbourhoodData}
                    clickedTract={null}
                    hoverMapLegend={hoverMapLegend}
                    setHoverMapLegend={setHoverMapLegend}
                />
            }
        </GoogleMap>
    )
};

export default React.memo(AllCompsMap);